import React, { useState, useEffect, useRef } from 'react';
import SwapBlock from './elements/SwapBlock';
import ProfileCard from './components/ProfileCard';
import StarField from './components/StarField';
import Splash from './Splash';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import { rateChannel, balChannel, useInitData } from './useSocket';

// Список всех иконок для предзагрузки
const COIN_ICONS = [
  'btc', 'eth', 'bnb', 'sol', 'xrp', 'ada', 'trx', 'ltc', 'dot', 'avax',
  'link', 'uni', 'aave', 'atom', 'xlm', 'algo', 'xmr', 'apt', 'sui', 'pol',
  'arb', 'inj', 'jup', 'fet', 'crv', 'gala', 'kas', 'hype', 'zora', 'plume',
  'home', 'red', 'era', 'axl', 'alt', 'doge', 'bch', 'near', 'icp', 'etc',
  'hbar', 'fil', 'op', 'vet', 'stx', 'imx', 'grt', 'render', 'tia', 'sei',
  'ldo', 'ondo', 'ena', 'wld', 'tao', 'pendle', 'jto', 'pyth', 'rune', 'wif',
  'pengu', 'trump', 'ar', 'ens', 'dydx', 'shib', 'pepe', 'bonk', 'floki',
  'usdt', 'apex'
];

// Индексы exchange иконок (0-21)
const EXCHANGE_ICONS = Array.from({ length: 22 }, (_, i) => i);

// Функция предзагрузки изображений
const preloadImages = () => {
  // Загружаем иконки монет
  COIN_ICONS.forEach(icon => {
    const img = new Image();
    img.src = `/access/coinIcon/${icon}.png`;
  });
  // Загружаем иконки бирж
  EXCHANGE_ICONS.forEach(idx => {
    const img = new Image();
    img.src = `/access/indexExchange/${idx}.svg`;
  });
  // Также загружаем logo
  const logo = new Image();
  logo.src = '/access/logo.png';
};

function App() {
  const [activeTab, setActiveTab] = useState('swap');
  const [showSplash, setShowSplash] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  // safe-area из JS-API Telegram (может быть 0 на части устройств/версий — тогда
  // выручает нативный env(safe-area-inset-top) в CSS + фолбэк на высоту панели Telegram).
  const [tgSaTop, setTgSaTop] = useState(0);   // отступ устройства (чёлка/Dynamic Island)
  const [tgCsaTop, setTgCsaTop] = useState(0); // отступ под UI Telegram (Закрыть/∨/…)
  const [isFs, setIsFs] = useState(false);     // включён ли фуллскрин
  const videoRef = useRef(null);

  useEffect(() => {
    const tg = window?.Telegram?.WebApp;
    if (!tg) return;
    const update = () => {
      try {
        setTgSaTop(Number(tg.safeAreaInset && tg.safeAreaInset.top) || 0);
        setTgCsaTop(Number(tg.contentSafeAreaInset && tg.contentSafeAreaInset.top) || 0);
        // фуллскрин: точный флаг, иначе — по наличию метода (Bot API 8.0+, мы его вызываем)
        setIsFs(typeof tg.isFullscreen === 'boolean' ? tg.isFullscreen : (typeof tg.requestFullscreen === 'function'));
      } catch (e) {}
    };
    update();
    const evs = ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged'];
    try { evs.forEach((e) => tg.onEvent && tg.onEvent(e, update)); } catch (e) {}
    // несколько повторов — фуллскрин/инсеты приходят с задержкой после requestFullscreen
    const timers = [200, 500, 1000, 1800, 3200].map((ms) => setTimeout(update, ms));
    return () => {
      timers.forEach(clearTimeout);
      try { evs.forEach((e) => tg.offEvent && tg.offEvent(e, update)); } catch (e) {}
    };
  }, []);

  // Накладной UI Telegram (Закрыть/∨/⋯) в фуллскрине рисуется ПОВЕРХ контента.
  // contentSafeAreaInset.top на части клиентов = 0 или приходит с задержкой, а
  // tg.isFullscreen местами врёт — поэтому детектим по ПЛАТФОРМЕ: на мобиле мы
  // всегда запрашиваем фуллскрин, значит полосу под кнопки Telegram резервируем всегда.
  const _tg = window?.Telegram?.WebApp;
  const _plat = String((_tg && _tg.platform) || '').toLowerCase();
  const _isMobile = _plat === 'ios' || _plat === 'android';
  const _hasFS = !!(_tg && typeof _tg.requestFullscreen === 'function');
  const _needBand = _isMobile && (isFs || _hasFS);
  // Полоса под кнопки Telegram: не меньше 60px на мобиле, даже если API вернул 0.
  const teleUI = _needBand ? Math.max(tgCsaTop, 60) : tgCsaTop;
  // Отступ устройства (чёлка/остров) — из нативного env(safe-area-inset-top)
  // (нужен viewport-fit=cover) ИЛИ из JS safeAreaInset. Плюс полоса Telegram + зазор.
  // На мобиле гарантируем минимум 104px, чтобы навбар точно был НИЖЕ кнопок Telegram.
  const _minPad = _needBand ? 104 : 13;
  const topPad = `max(calc(max(env(safe-area-inset-top, 0px), ${tgSaTop}px) + ${teleUI + 10}px), ${_minPad}px)`;

  window?.Telegram?.WebApp?.ready();
  window?.Telegram?.WebApp?.expand();
  try {
    const tg = window?.Telegram?.WebApp;
    tg?.setBackgroundColor?.('#0E1A22');
    tg?.setHeaderColor?.('#0E1A22');
    tg?.setBottomBarColor?.('#0A1117');
    tg?.requestFullscreen?.();
    tg?.disableVerticalSwipes?.();
  } catch (e) {}

  // Предзагрузка всех иконок при монтировании компонента (параллельно с видео)
  useEffect(() => {
    preloadImages();
  }, []);

  useEffect(() => {
    setPageLoaded(true);
  }, []);

  useInitData();

  // Заставка: анимация (частицы + свечи + прогресс) + предзагрузка ВСЕХ иконок,
  // минимум ~3с, завершается только когда и время прошло, и иконки загружены.
  if (showSplash) {
    return <Splash onDone={() => setShowSplash(false)} minMs={3000} />;
  }

  // После завершения заставки отображаем основной контент
  return (
    <div style={{
      padding: '13px',
      paddingTop: topPad,
      paddingBottom: '90px',
      minHeight: '100vh',
    }}>
      <StarField density={1.15} topBias={1.4} />
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
          @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .tab-content {
          animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-button:active {
          transform: scale(0.98);
        }
        * {
          scroll-behavior: smooth;
        }
        .balance-toggle:focus {
          outline: none;
        }
        .balance-toggle:focus-visible {
          outline: none;
          box-shadow: none;
        }
      `}</style>

      {/* Контент вкладок с плавным переключением */}
      <div style={{ position: 'relative', minHeight: 'calc(100vh - 120px)' }}>
        <div
          className="tab-content"
          key={activeTab}
          style={{
            opacity: 1,
            transition: 'opacity 0.3s ease',
          }}
        >
          {activeTab === 'swap' && (
            <SwapBlock
              wsRate={rateChannel}
              wsBalance={balChannel}
              onDepositClick={() => {
                setActiveTab('profile');
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('openDepositModal'));
                }, 120);
              }}
              onWithdrawClick={() => {
                setActiveTab('profile');
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('openWithdrawModal'));
                }, 120);
              }}
            />
          )}
          {activeTab === 'profile' && <ProfileCard wsRate={rateChannel} wsBalance={balChannel} />}
        </div>
      </div>
    </div>
  );
}

export default App;
